"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";

import { useDashboardBuilderOptional } from "@/contexts/DashboardBuilderContext";
import type { DashboardWidgetDto } from "@/lib/api/types";

import { useDashboardRuntime } from "./DashboardRuntimeContext";
import {
  type DashboardTelemetryBatchContextValue,
  useDashboardData,
} from "@/hooks/useDashboardData";

const DashboardTelemetryBatchContext = createContext<DashboardTelemetryBatchContextValue | null>(null);

export function useDashboardTelemetryBatchOptional(): DashboardTelemetryBatchContextValue | null {
  return useContext(DashboardTelemetryBatchContext);
}

type DashboardTelemetryBatchProviderProps = {
  tenantId: string;
  dashboardId: string;
  widgets: DashboardWidgetDto[];
  children: ReactNode;
};

export function DashboardTelemetryBatchProvider({
  tenantId,
  dashboardId,
  widgets,
  children,
}: DashboardTelemetryBatchProviderProps) {
  const rt = useDashboardRuntime();
  const builder = useDashboardBuilderOptional();
  const mergedWidgets = useMemo(
    () => widgets.map((w) => (builder ? builder.getDisplayWidget(w) : w)),
    [widgets, builder],
  );

  const batchState = useDashboardData({
    tenantId,
    dashboardId,
    widgets: mergedWidgets,
    globalTimeRange: rt?.globalTimeRange ?? null,
  });

  return (
    <DashboardTelemetryBatchContext.Provider value={batchState}>{children}</DashboardTelemetryBatchContext.Provider>
  );
}
